/**
 * Item Filter Service
 * Handles filtering logic and state management for items
 */

const itemFilterService = (() => {
    // Apply status filter to items
    const applyStatusFilter = (items, statusFilter) => {
        if (!statusFilter) {
            return items;
        }

        // Convert status key to ID if needed
        const statusId = typeof statusFilter === 'string' ? statusFilter : statusFilter;

        if (!statusId) {
            return items;
        }

        return items.filter(item => item.status && item.status.includes(statusId));
    };

    // Apply type filter to items
    const applyTypeFilter = (items, typeFilter) => {
        if (!typeFilter) {
            return items;
        }

        return items.filter(item => item.type === typeFilter);
    };

    // Apply stock filter to items
    const applyStockFilter = (items, stockFilter) => {
        if (!stockFilter) {
            return items;
        }

        const { hideOutOfStock, minStock, maxStock } = stockFilter;

        return items.filter(item => {
            // Hide out of stock items
            if (hideOutOfStock && item.stock === 0) {
                return false;
            }

            // Min stock filter
            if (minStock !== undefined && item.stock < minStock) {
                return false;
            }

            // Max stock filter
            if (maxStock !== undefined && item.stock > maxStock) {
                return false;
            }

            return true;
        });
    };

    // Apply price filter to items
    const applyPriceFilter = (items, priceFilter) => {
        if (!priceFilter) {
            return items;
        }

        const { minPrice, maxPrice } = priceFilter;

        return items.filter(item => {
            if (minPrice !== undefined && item.price < minPrice) {
                return false;
            }

            if (maxPrice !== undefined && item.price > maxPrice) {
                return false;
            }

            return true;
        });
    };

    // Apply date range filter to items
    const applyDateFilter = (items, dateFilter) => {
        if (!dateFilter) {
            return items;
        }

        const { startDate, endDate, field = 'date' } = dateFilter;

        return items.filter(item => {
            const itemDate = new Date(item[field]);

            if (startDate && itemDate < new Date(startDate)) {
                return false;
            }

            if (endDate && itemDate > new Date(endDate)) {
                return false;
            }

            return true;
        });
    };

    // Apply multiple filters to items
    const applyAllFilters = (items, filters) => {
        let filteredItems = [...items];

        // Apply status filter
        if (filters.status) {
            filteredItems = applyStatusFilter(filteredItems, filters.status);
        }

        // Apply type filter
        if (filters.type) {
            filteredItems = applyTypeFilter(filteredItems, filters.type);
        }

        // Apply stock filter
        if (filters.stock) {
            filteredItems = applyStockFilter(filteredItems, filters.stock);
        }

        // Apply price filter
        if (filters.price) {
            filteredItems = applyPriceFilter(filteredItems, filters.price);
        }

        // Apply date filter
        if (filters.date) {
            filteredItems = applyDateFilter(filteredItems, filters.date);
        }

        return filteredItems;
    };

    // Get filter summary
    const getFilterSummary = (items, filteredItems, filters) => {
        const totalItems = items.length;
        const filteredCount = filteredItems.length;
        const activeFilters = Object.entries(filters)
            .filter(([key, value]) => value !== null && value !== undefined && value !== '')
            .map(([key, value]) => ({ key, value }));

        return {
            totalItems,
            filteredCount,
            activeFilters,
            hasActiveFilters: activeFilters.length > 0,
            filterPercentage: totalItems > 0 ? Math.round((filteredCount / totalItems) * 100) : 0
        };
    };

    // Clear all filters
    const clearAllFilters = () => {
        return {
            status: null,
            type: null,
            stock: null,
            price: null,
            date: null
        };
    };

    // Get filter options for UI
    const getFilterOptions = items => {
        const statusOptions = [...new Set(items.flatMap(item => item.status || []))];
        const typeOptions = [...new Set(items.map(item => item.type).filter(Boolean))];
        const priceRange = items.reduce(
            (acc, item) => {
                if (item.price !== undefined) {
                    acc.min = Math.min(acc.min, item.price);
                    acc.max = Math.max(acc.max, item.price);
                }
                return acc;
            },
            { min: Infinity, max: -Infinity }
        );

        return {
            status: statusOptions,
            type: typeOptions,
            priceRange: priceRange.min === Infinity ? null : priceRange
        };
    };

    return {
        applyStatusFilter,
        applyTypeFilter,
        applyStockFilter,
        applyPriceFilter,
        applyDateFilter,
        applyAllFilters,
        getFilterSummary,
        clearAllFilters,
        getFilterOptions
    };
})();

export default itemFilterService;
